import { NavigationState } from '@react-navigation/native';
import Constants from 'expo-constants';
import { Platform } from 'react-native';

import { navigationRef } from 'app/navigation/navigationHelper';
import { Events, EventsByValue, IAnalytics, NavigationRoute } from 'app/services/Analytics.types';
import { getAnalytics, logEvent, setUserId, setUserProperties } from 'app/services/Firebase';
import HeapAnalytics from 'app/services/HeapAnalytics';
import InsiderService from 'app/services/Insider';
import Logger from 'app/services/Logger';
import OneTrust from 'app/services/OneTrust';

export { Events, UserProps } from 'app/services/Analytics.types';

const MODULE = `[Analytics]`;
const DEFAULT_ROUTE = 'UNKNOWN';

const waitForSDK = (
  sdk: string,
  callback: () => void,
  timeout = 10000,
  interval = 100
): Promise<void> => {
  const start = Date.now();

  return new Promise((resolve, reject) => {
    const check = () => {
      const loaded = !!window[sdk as keyof typeof window];
      if (loaded) {
        callback();
        resolve();
      } else if (Date.now() - start >= timeout) {
        reject(new Error(`Timeout waiting for global: ${sdk}`));
      } else {
        setTimeout(check, interval);
      }
    };

    check();
  });
};

const Analytics: IAnalytics = {
  init: () => {
    Logger.debug(`${MODULE} init`);
    HeapAnalytics.init();

    if (Platform.OS === 'web') {
      waitForSDK('OneTrust', () => {
        OneTrust.onConsentChanged(() => {
          Analytics.flushQueue();
        });
      }).catch((err: unknown) => {
        Logger.warn(`${MODULE} OneTrust SDK not available, consent listener not registered`, {
          err,
        });
      });
    }

    if (__DEV__) {
      Logger.debug(`${MODULE} setting userId to "developer"`);
      Analytics.setUserId('developer');
    }
  },

  trackEvent: (event, data) => {
    const route = navigationRef.getCurrentRoute();
    // eslint-disable-next-line no-param-reassign
    data = Object.assign(data ?? {}, { firebase_screen_class: route?.name || DEFAULT_ROUTE });

    try {
      Logger.debug(`${MODULE} track event ${event}`, data);
      logEvent(getAnalytics(), event as string, data || {});
      HeapAnalytics.track(event, data || {});
    } catch (error) {
      Logger.error(`${MODULE} track failed`, { error, event, data });
    }
  },

  setUserId: (id) => {
    try {
      Logger.debug(`${MODULE} setUserId`, { id });
      setUserId(getAnalytics(), id);
      HeapAnalytics.identify(id);
    } catch (error) {
      Logger.error(`${MODULE} setUserId failed`, { error, id });
    }
  },

  setUserProperty: (key, value) => {
    try {
      Logger.debug(`${MODULE} setUserProperty`, { key, value });
      setUserProperties(getAnalytics(), { [key]: `${value}` });
      HeapAnalytics.addUserProperties({ [key]: value });
    } catch (error) {
      Logger.error(`${MODULE} setUserProperty failed`, { error, key, value });
    }
  },

  setUserProperties: (props) => {
    try {
      Logger.debug(`${MODULE} setUserProperties`, { ...props });
      setUserProperties(getAnalytics(), mapValuesToString(props));
      HeapAnalytics.addUserProperties(props);
    } catch (error) {
      Logger.error(`${MODULE} setUserProperties failed`, { error, ...props });
    }
  },

  onScreenChange: (state) => {
    const route = getActiveRoute(state);

    if (route) {
      Logger.debug(`${MODULE} onScreenChange`, route);
      Analytics.trackEvent(Events.ScreenView, {
        screen_name: route.name,
        firebase_screen: `${route.name}`,
        firebase_screen_class: `${route.name}`,
        appVersion: Constants.expoConfig?.version ?? 'UNKNOWN',
        sdkVersion: Constants.expoConfig?.sdkVersion ?? 'UNKNOWN',
        ...sanitizeRouteParams(route.params),
      });
    } else {
      Logger.debug(`${MODULE} unable to find screen route name`);
    }
  },

  trackEventFromLinkPress: (htmlAttributes, data) => {
    const event = htmlAttributes?.['data-track-event'];
    if (!event) return;

    if (Analytics.isEvent(event)) {
      Analytics.trackEvent(event, data);
      return;
    }
    Logger.warn(`${MODULE} unknown data-track-event on link`, { htmlAttributes, data });
  },

  isEvent(value: unknown): value is Events {
    return typeof value === 'string' && value in EventsByValue;
  },

  flushQueue: () => {
    waitForSDK('Insider', () => InsiderService.flushQueue?.());
    waitForSDK('heap', () => HeapAnalytics.flushQueue?.());
  },
};

Analytics.init();

export default Analytics;

function getActiveRoute(state?: NavigationState): Maybe<NavigationRoute> {
  if (!state) return null;
  const route = state.routes[state.index];

  if (isNavState(route.state)) {
    return getActiveRoute(route.state);
  }

  return route;
}

function isNavState(value: unknown): value is NavigationState {
  return typeof value === 'object' && value !== null && 'routes' in value;
}

function mapValuesToString(data: Record<string, unknown>) {
  return Object.entries(data).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: `${value}`,
    }),
    {}
  );
}

function sanitizeRouteParams($params: Readonly<object | undefined> = {}) {
  const params: Record<string, string> = { ...$params };

  if (params.productId) {
    params.productKey = params.productId;
    delete params.productId;
  }

  if (params.attractionId) {
    params.attractionKey = params.attractionId;
    delete params.attractionId;
  }

  return params;
}
